import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getAuthToken, redirectToLogin, logout as authLogout } from './authTokenService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5117';

/**
 * Create a configured axios instance with Bearer token authentication.
 * Automatically attaches stored token and handles 401 by redirecting to login.
 */
export const createAuthenticatedAxios = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Attach Bearer token to every request
  instance.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle 401 — token expired or missing → redirect to login
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        redirectToLogin();
        return Promise.reject(new Error('Authentication required - redirecting to login'));
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * Shared authenticated axios instance for all services.
 */
export const authenticatedAxios = createAuthenticatedAxios();

/**
 * Get axios config with optional additional headers and timeout.
 */
export const getAxiosConfig = (
  additionalHeaders: Record<string, string> = {},
  timeout?: number
): AxiosRequestConfig => {
  const token = getAuthToken();
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...additionalHeaders,
    },
  };

  if (timeout) {
    config.timeout = timeout;
  }

  return config;
};

/**
 * Logout by clearing token and redirecting to Easy Auth logout.
 */
export const logout = (): void => {
  authLogout();
};

export { API_BASE_URL };
